import React, { useEffect, useState, useRef, useCallback } from "react";
import { useCanvas } from "../hooks/useCanvas";
import { useCanvasElements } from "../hooks/useCanvasElements";
import { useOpenAgProject } from "../hooks/useOpenAgProject";
import useDesktopCheck from "../hooks/useDesktopCheck";
import CanvasToolbar from "../components/Canvas/CanvasToolbar/CanvasToolbar";
import SidebarNavigation from "../components/Canvas/SideBarNavigation/SideBarNavigation";
import LibraryPanel from "../components/Canvas/LibraryPanel/LibraryPanel";
import CanvasArea from "../components/Canvas/CanvasArea/CanvasArea";
import Timeline from "../components/Canvas/Timeline/Timeline";
import PropertiesPanel from "../components/Canvas/PropertiesPanel/PropertiesPanel";
import LoadingSpinner from "../components/UI/LoadingSpinner";
import MultiOptionModal from "../components/UI/MultiOptionModal";
import LoadingProgressModal from "../components/UI/LoadingProgressModal";
import SpriteLoadingIndicator from "../components/UI/SpriteLoadingIndicator";
import DesktopRequiredScreen from "../components/UI/DesktopRequiredScreen";
import ComponentUpdateIndicator from "../components/UI/ComponentUpdateIndicator";
import MediaSelectorModal from "../components/Canvas/PropertiesPanel/MediaSelectorModal";
import CanvasTools from "../components/Canvas/CanvasTools/CanvasTools";
import ResizeHandle from "../components/UI/ResizeHandle";
import CollapseButton from "../components/UI/CollapseButton";

// Panel size constraints
const LIBRARY_MIN = 180;
const LIBRARY_MAX = 480;
const LIBRARY_DEFAULT = 256;
const PROPERTIES_MIN = 180;
const PROPERTIES_MAX = 480;
const PROPERTIES_DEFAULT = 256;
const TIMELINE_MIN_PCT = 15;
const TIMELINE_MAX_PCT = 65;
const TIMELINE_DEFAULT_PCT = 40;

const CanvasPage = () => {
  const [activeTab, setActiveTab] = useState("elements");
  const [activeTool, setActiveTool] = useState("selector");
  const [searchQuery, setSearchQuery] = useState("");

  // Resizable panel widths/heights
  const [libraryWidth, setLibraryWidth] = useState(LIBRARY_DEFAULT);
  const [propertiesWidth, setPropertiesWidth] = useState(PROPERTIES_DEFAULT);
  const [timelinePct, setTimelinePct] = useState(TIMELINE_DEFAULT_PCT);

  // Collapsible panel states
  const [libraryCollapsed, setLibraryCollapsed] = useState(false);
  const [propertiesCollapsed, setPropertiesCollapsed] = useState(false);
  const [timelineCollapsed, setTimelineCollapsed] = useState(false);

  // Center area ref for timeline vertical resize calculation
  const centerRef = useRef(null);

  // Resize handlers
  const handleLibraryResize = useCallback((delta) => {
    setLibraryWidth((w) => Math.min(LIBRARY_MAX, Math.max(LIBRARY_MIN, w + delta)));
  }, []);

  const handlePropertiesResize = useCallback((delta) => {
    // Dragging left edge left = wider, so negate delta
    setPropertiesWidth((w) => Math.min(PROPERTIES_MAX, Math.max(PROPERTIES_MIN, w - delta)));
  }, []);

  const handleTimelineResize = useCallback((delta) => {
    if (!centerRef.current) return;
    const centerHeight = centerRef.current.getBoundingClientRect().height;
    const deltaPct = (delta / centerHeight) * -100; // dragging up = larger timeline
    setTimelinePct((pct) => Math.min(TIMELINE_MAX_PCT, Math.max(TIMELINE_MIN_PCT, pct + deltaPct)));
  }, []);

  // Check if user is on mobile or portrait orientation
  const isMobileOrPortrait = useDesktopCheck();

  const mediaSelectorRef = useRef(null);
  const elementToReplaceRef = useRef(null);


  // Canvas presets for name mapping
  const canvasPresets = [
    { name: 'Youtube Post', width: 1920, height: 1080 },
    { name: 'Square', width: 1080, height: 1080 },
    { name: 'Portrait', width: 1080, height: 1920 },
    { name: 'Youtube 4K', width: 3840, height: 2160 },
  ];

  // Helper function to get canvas name from dimensions - always includes dimensions in parentheses
  const getCanvasNameFromDimensions = (width, height) => {
    const preset = canvasPresets.find(
      preset => preset.width === width && preset.height === height
    );
    const baseName = preset ? preset.name : 'Custom';
    return `${baseName} (${width}×${height})`;
  };

  // Get canvas state and operations - no containerRef so won't initialize PIXI
  const {
    projectName,
    setProjectName,
    requestAddItem,
    viewport,
    pixiInitialized,
    setCanvasSize,
    setCanvasBackgroundColor,
    clearHistory,
    updateElement,
    canvasSize
  } = useCanvas();

  // Call useCanvasElements ONLY here - this is the single source of truth
  const {
    addComponentToCanvas,
    handleCanvasDrop,
    handleDragOver,
    createVideoSprite,
    createImageSprite,
    createAudioSprite,
    createGifSprite,
    showSizeMismatchModal,
    pendingTemplate,
    handleChangeCanvasSizeAndAdd,
    handleContinueWithoutChange,
    handleCancelTemplateDrop,
    templateLoadingProgress,
    spriteLoadingItems,
    componentUpdateItems,
    replaceElementMedia,
  } = useCanvasElements(viewport);

  // Pass canvas operations to useOpenAgProject
  const { projectData, isLoading, initializationError, loadingProgress } = useOpenAgProject({
    setProjectName,
    setCanvasSize,
    setCanvasBackgroundColor,
    viewport,
    pixiInitialized,
    clearHistory,
    updateElement,
    addComponentToCanvas
  });

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
  };

  const handleReplaceMediaAction = useCallback((element) => {
    if (element && mediaSelectorRef.current) {
      elementToReplaceRef.current = element;
      const mediaType = element.type;
      const currentUrl = element.componentData?.url || element.componentData?.preview || '';

      // Build opts with the element's current dimensions and duration
      // so the crop/trim modal knows the constraints for this asset
      const opts = {};
      const sprite = element.pixiSprite;
      const texW = sprite?.texture?.source?.width || sprite?.texture?.width || 0;
      const texH = sprite?.texture?.source?.height || sprite?.texture?.height || 0;
      const elWidth =
        element.componentData?.dimensions?.width ||
        element.componentData?.width ||
        (texW > 0 ? texW : null) ||
        element.width || null;
      const elHeight =
        element.componentData?.dimensions?.height ||
        element.componentData?.height ||
        (texH > 0 ? texH : null) ||
        element.height || null;
      if (elWidth) opts.targetWidth = elWidth;
      if (elHeight) opts.targetHeight = elHeight;

      const FPS = 25;
      const elDuration =
        element.componentData?.duration ||
        (element.componentData?.frameDuration
          ? element.componentData.frameDuration / FPS
          : null) ||
        element.timeline?.duration || null;
      if (elDuration) opts.maxDuration = elDuration;

      mediaSelectorRef.current.open(mediaType, currentUrl, (result, newName) => {
        if (result && elementToReplaceRef.current) {
          const url = typeof result === 'string' ? result : result?.url;
          const thumbnailUrl = typeof result === 'object' ? result?.thumbnailUrl : null;
          if (url) replaceElementMedia(elementToReplaceRef.current.id, url, elementToReplaceRef.current.type, newName, thumbnailUrl);
        }
        elementToReplaceRef.current = null;
      }, { ...opts, layerKey: element.id });
    }
  }, [replaceElementMedia]);

  // Show mobile/portrait restriction screen
  if (isMobileOrPortrait) {
    return <DesktopRequiredScreen feature="the canvas editor" />;
  }

  // Show loading spinner while project is loading
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-ag-black-800">
        <div className="flex flex-col items-center justify-center">
          <LoadingSpinner size="lg" />
          <p className="text-gray-400 mt-4">Loading project...</p>
        </div>
      </div>
    );
  }

  // Show error if project failed to load
  if (initializationError) {
    return (
      <div className="flex items-center justify-center h-screen bg-ag-black-800">
        <div className="text-center">
          <p className="text-red-500 mb-2">Failed to load project</p>
          <p className="text-gray-400">{initializationError}</p>
        </div>
      </div>
    );
  }

  // Computed sizes for collapsed states
  const effectiveLibraryWidth = libraryCollapsed ? 0 : libraryWidth;
  const effectivePropertiesWidth = propertiesCollapsed ? 0 : propertiesWidth;
  const effectiveTimelinePct = timelineCollapsed ? 0 : timelinePct;
  const canvasHeightPct = 100 - effectiveTimelinePct;

  return (
    <>
      <div className="h-screen flex flex-col bg-ag-black-700">
        <CanvasToolbar />
        <div className="flex-1 flex overflow-hidden">
          <SidebarNavigation
            activeTab={activeTab}
            onTabChange={handleTabChange}
          />

          {/* Left Sidebar - Library Panel (resizable + collapsible) */}
          <div className="relative z-20 flex-shrink-0" style={{ width: effectiveLibraryWidth, transition: libraryCollapsed ? "width 0.2s ease" : undefined }}>
            <div
              className="bg-ag-black-700 border-r border-ag-black-400 flex flex-col h-full overflow-hidden"
              style={{
                minWidth: libraryCollapsed ? 0 : LIBRARY_MIN,
                maxWidth: LIBRARY_MAX,
              }}
            >
              {!libraryCollapsed && (
                <LibraryPanel
                  activeTab={activeTab}
                  searchQuery={searchQuery}
                  setSearchQuery={setSearchQuery}
                  onItemDoubleClick={requestAddItem}
                />
              )}
            </div>
            {/* Collapse button - always visible outside overflow */}
            <CollapseButton
              collapseDirection="left"
              isCollapsed={libraryCollapsed}
              onClick={() => setLibraryCollapsed((c) => !c)}
              position={libraryCollapsed ? "top-2 left-1" : "top-1 right-1"}
              title={libraryCollapsed ? "Show library" : "Hide library"}
            />
            {/* Resize handle on the right edge */}
            {!libraryCollapsed && (
              <ResizeHandle
                direction="horizontal"
                onResize={handleLibraryResize}
                className="right-0"
              />
            )}
          </div>

          {/* Center Area - Canvas and Timeline with proper spacing */}
          <div ref={centerRef} className="flex-1 flex flex-col bg-ag-black-800 overflow-hidden min-w-0">
            {/* Canvas area - fills remaining space */}
            <div
              className={`relative overflow-hidden ${(loadingProgress?.isOpen || templateLoadingProgress?.isOpen) ? 'pointer-events-none opacity-50' : ''}`}
              style={{ height: `${canvasHeightPct}%` }}
            >
              <CanvasArea
                handleCanvasDrop={handleCanvasDrop}
                handleDragOver={handleDragOver}
                createVideoSprite={createVideoSprite}
                createImageSprite={createImageSprite}
                createAudioSprite={createAudioSprite}
                createGifSprite={createGifSprite}
                onReplaceAction={handleReplaceMediaAction}
                addComponentToCanvas={addComponentToCanvas}
              />
              {/* Floating toolbar above the timeline */}
              <div className="absolute bottom-1 left-1/2 -translate-x-1/2 z-30">
                <CanvasTools activeTool={activeTool} onToolChange={setActiveTool} />
              </div>
            </div>

            {/* Timeline (resizable + collapsible) */}
            <div
              className="rounded-t-lg bg-ag-black-700 border-t border-ag-black-400 relative z-20 overflow-hidden"
              style={{
                height: timelineCollapsed ? 28 : `${effectiveTimelinePct}%`,
                minHeight: timelineCollapsed ? 28 : undefined,
                transition: timelineCollapsed ? "height 0.2s ease" : undefined,
              }}
            >
              {/* Collapse button - top-right corner of timeline */}
              <CollapseButton
                collapseDirection="down"
                isCollapsed={timelineCollapsed}
                onClick={() => setTimelineCollapsed((c) => !c)}
                position="top-1 right-2"
                title={timelineCollapsed ? "Show timeline" : "Hide timeline"}
              />
              {/* Resize handle on the top edge */}
              {!timelineCollapsed && (
                <ResizeHandle
                  direction="vertical"
                  onResize={handleTimelineResize}
                  className="top-0"
                />
              )}
              {!timelineCollapsed && (
                <Timeline
                  createVideoSprite={createVideoSprite}
                  createImageSprite={createImageSprite}
                  createAudioSprite={createAudioSprite}
                  createGifSprite={createGifSprite}
                  onReplaceAction={handleReplaceMediaAction}
                  addComponentToCanvas={addComponentToCanvas}
                />
              )}
              {timelineCollapsed && (
                <div className="h-full flex items-center px-10 text-gray-500 text-xs select-none">
                  Timeline
                </div>
              )}
            </div>
          </div>

          {/* Right Sidebar - Properties Panel (resizable + collapsible) */}
          <div className="relative z-20 flex-shrink-0" style={{ width: effectivePropertiesWidth, transition: propertiesCollapsed ? "width 0.2s ease" : undefined }}>
            <div
              className="bg-ag-black-700 border-l border-ag-black-400 flex flex-col h-full overflow-hidden"
              style={{
                minWidth: propertiesCollapsed ? 0 : PROPERTIES_MIN,
                maxWidth: PROPERTIES_MAX,
              }}
            >
              {!propertiesCollapsed && (
                <PropertiesPanel replaceElementMedia={replaceElementMedia} />
              )}
            </div>
            {/* Collapse button - always visible outside overflow */}
            <CollapseButton
              collapseDirection="right"
              isCollapsed={propertiesCollapsed}
              onClick={() => setPropertiesCollapsed((c) => !c)}
              position={propertiesCollapsed ? "top-2 right-1" : "top-1 left-1"}
              title={propertiesCollapsed ? "Show properties" : "Hide properties"}
            />
            {/* Resize handle on the left edge */}
            {!propertiesCollapsed && (
              <ResizeHandle
                direction="horizontal"
                onResize={handlePropertiesResize}
                className="left-0"
              />
            )}
          </div>
        </div>
      </div>

      {/* Canvas Size Mismatch Modal */}
      <MultiOptionModal
        isOpen={showSizeMismatchModal}
        onClose={handleCancelTemplateDrop}
        title="Canvas Size Mismatch"
        message={
          pendingTemplate && pendingTemplate.canvas ? (
            <div className="space-y-2">
              <p className="text-gray-300">
                The template you're trying to add was designed for a different canvas size:
              </p>
              <div className="bg-ag-black-700 p-3 rounded-md text-sm border border-ag-black-400">
                <div className="flex justify-between mb-1">
                  <span className="font-medium text-gray-300">Current canvas:</span>
                  <span className="text-gray-400">
                    {canvasSize.name || `${canvasSize.width} × ${canvasSize.height}`}
                  </span>
                </div>
                <div className="flex justify-between mb-1">
                  <span className="font-medium text-gray-300">Template canvas:</span>
                  <span className="text-ag-green">
                    {pendingTemplate.canvas.name || `${pendingTemplate.canvas.width} × ${pendingTemplate.canvas.height}`}
                  </span>
                </div>
              </div>
              <p className="text-sm text-gray-400">
                Adding the template without changing the canvas size may cause elements to appear incorrectly positioned or cropped.
              </p>
            </div>
          ) : (
            'Canvas size mismatch detected.'
          )
        }
        options={[
          {
            text: 'Change Canvas Size & Add Template',
            onClick: handleChangeCanvasSizeAndAdd,
            variant: 'primary'
          },
          {
            text: 'Continue Without Changing Size',
            onClick: handleContinueWithoutChange,
            variant: 'outline'
          },
          {
            text: 'Cancel',
            onClick: handleCancelTemplateDrop,
            variant: 'ghost'
          }
        ]}
        type="warning"
      />

      {/* Loading Progress Modal for Projects */}
      <LoadingProgressModal
        isOpen={loadingProgress?.isOpen || false}
        progress={loadingProgress?.progress || 0}
        currentAsset={loadingProgress?.currentAsset || ''}
        totalAssets={loadingProgress?.totalAssets || 0}
        loadedAssets={loadingProgress?.loadedAssets || 0}
        stage={loadingProgress?.stage || 'initializing'}
      />

      {/* Loading Progress Modal for Templates */}
      <LoadingProgressModal
        isOpen={templateLoadingProgress?.isOpen || false}
        progress={templateLoadingProgress?.progress || 0}
        currentAsset={templateLoadingProgress?.currentAsset || ''}
        totalAssets={templateLoadingProgress?.totalAssets || 0}
        loadedAssets={templateLoadingProgress?.loadedAssets || 0}
        stage={templateLoadingProgress?.stage || 'initializing'}
      />

      {/* Sprite Loading Indicator - Toast notifications for individual media items */}
      <SpriteLoadingIndicator loadingItems={spriteLoadingItems} />

      {/* Component Update Indicator - Toast notifications for component re-renders */}
      <ComponentUpdateIndicator updateItems={componentUpdateItems} />

      <MediaSelectorModal ref={mediaSelectorRef} />
    </>
  );
};

export default CanvasPage;
